import type { OnboardingStep, Shopping } from '../../shared/types.ts'

export interface Option { value: string; label: string }
export interface OptionGroup { label?: string; single?: boolean; options: Option[] }

export interface StepScript {
  step: OnboardingStep
  /** What the assistant says when this step opens. */
  prompt: string
  hint: string
  groups: OptionGroup[]
  placeholder: string
  /** Answering with nothing selected is allowed, and says this instead. */
  skipLabel?: string
  /** Single-choice steps submit the moment you tap. */
  instant?: boolean
}

const opts = (...values: string[]): Option[] => values.map((v) => ({ value: v, label: v }))
const prefixed = (prefix: string, ...values: string[]): Option[] =>
  values.map((v) => ({ value: `${prefix}:${v}`, label: v }))

export const SHOPPING_LABEL: Record<Shopping, string> = {
  none: "Use what's here",
  quick: 'A couple of things',
  happy: 'Happy to shop',
}

export const SCRIPT: StepScript[] = [
  {
    step: 'equipment',
    prompt: "First, your kitchen. What can you actually cook with?",
    hint: 'Tap what you have — or describe it, quirks and all.',
    placeholder: 'an air fryer, a wok, and an oven that runs hot…',
    groups: [
      { label: 'Heat', options: opts('oven', 'stovetop', 'microwave', 'air fryer', 'pressure cooker', 'slow cooker', 'rice cooker', 'grill', 'toaster oven') },
      { label: 'Pans', options: opts('nonstick skillet', 'cast iron skillet', 'saucepan', 'stockpot', 'wok', 'dutch oven', 'sheet pan', 'baking dish') },
      { label: 'Tools', options: opts('chef knife', 'blender', 'food processor', 'stand mixer', 'immersion blender', 'kitchen scale', 'thermometer', 'mixing bowls') },
    ],
  },
  {
    step: 'limits',
    prompt: "Anything you can't or won't eat?",
    hint: 'Allergies first — those are absolute. Then anything you just avoid.',
    placeholder: "severe peanut allergy, and I don't do offal…",
    skipLabel: 'Nothing off limits',
    groups: [
      { label: 'Allergies', options: prefixed('allergy', 'peanuts', 'tree nuts', 'shellfish', 'eggs', 'soy', 'sesame', 'gluten', 'dairy', 'fish') },
      { label: 'Diets', options: prefixed('diet', 'vegetarian', 'vegan', 'pescatarian', 'halal', 'kosher', 'gluten-free', 'dairy-free', 'low-carb') },
      { label: 'Just not for me', options: prefixed('dislike', 'cilantro', 'olives', 'mushrooms', 'blue cheese', 'liver', 'anchovies', 'raw fish') },
    ],
  },
  {
    step: 'taste',
    prompt: 'And what do you actually enjoy?',
    hint: 'This is the part that stops every suggestion tasting the same.',
    placeholder: 'anything with garlic and lemon, usually cooking for two on a weeknight…',
    groups: [
      { label: 'Flavours', options: prefixed('love', 'garlic', 'chilli', 'lemon', 'fresh herbs', 'butter', 'smoke', 'ginger', 'miso', 'cheese', 'vinegar') },
      { label: 'Cuisines', options: prefixed('cuisine', 'italian', 'japanese', 'mexican', 'thai', 'indian', 'french', 'korean', 'chinese', 'levantine', 'greek') },
      {
        label: 'How spicy',
        single: true,
        options: [
          { value: 'heat:0', label: 'no chilli' },
          { value: 'heat:1', label: 'mild' },
          { value: 'heat:2', label: 'medium' },
          { value: 'heat:3', label: 'hot' },
          { value: 'heat:4', label: 'very hot' },
          { value: 'heat:5', label: 'as hot as it comes' },
        ],
      },
      {
        label: 'In the kitchen',
        single: true,
        options: [
          { value: 'skill:beginner', label: 'still learning' },
          { value: 'skill:intermediate', label: 'comfortable' },
          { value: 'skill:advanced', label: 'confident' },
        ],
      },
    ],
  },
  {
    step: 'shopping',
    prompt: 'Last one. Before you cook, how do you feel about a shop run?',
    hint: 'This decides how far a recipe may stray from what you already have.',
    placeholder: 'depends on the day, but usually I cannot be bothered…',
    instant: true,
    groups: [
      {
        single: true,
        options: [
          { value: 'none', label: "Not happening — use what's here" },
          { value: 'quick', label: "I'll grab a couple of things" },
          { value: 'happy', label: 'Happy to shop properly' },
        ],
      },
    ],
  },
]

export const scriptFor = (step: OnboardingStep): StepScript =>
  SCRIPT.find((s) => s.step === step) ?? SCRIPT[0]

export const STEP_TITLE: Record<OnboardingStep, string> = {
  equipment: 'Your kitchen',
  limits: 'What you avoid',
  taste: 'What you like',
  shopping: 'Shopping',
}
